// dwaar/src/lib/suchi.js
// Repo: terralekha-core | Folder: dwaar/src/lib/
// Suchi — Sanskrit: सूची — catalogue, index, list.
//   Curated CN code catalogue for CBAM-relevant goods exported by Indian MSMEs.
//   Sector-keyed. Each entry: { code: "XXXXXXXX", desc: "Plain English description" }.
//
// Source: EU Regulation 2023/956 Annex I + DGFT India export statistics.
// Curation principle: top codes by Indian MSME export volume, not exhaustive.
//   Steel has 478 CBAM codes; showing 478 to a first-time MSME user is noise.
//   We show the 20 most commonly exported headings — users can add others later.
//
// Reveal doctrine: this file is client-side. No emission factors here.
//   Factor values live in FactorStore (server-side only).

// ── Steel ─────────────────────────────────────────────────────────────────────
// Chapter 72 (iron & steel base) + Chapter 73 (iron & steel articles)
// Most common Indian MSME exports: hot-rolled coils, rebar, structural sections,
// wire rod, cold-rolled sheet, tubes & pipes, fittings.

const STEEL = [
  { code: "72081000", desc: "Hot-rolled coils — iron/non-alloy steel ≥600 mm, with patterns in relief" },
  { code: "72082500", desc: "Hot-rolled coils — iron/non-alloy steel ≥600 mm, ≥4.75 mm thick" },
  { code: "72083600", desc: "Hot-rolled sheet — iron/non-alloy steel ≥600 mm, 3–4.75 mm thick" },
  { code: "72083700", desc: "Hot-rolled sheet — iron/non-alloy steel ≥600 mm, 2–3 mm thick" },
  { code: "72083800", desc: "Hot-rolled sheet — iron/non-alloy steel ≥600 mm, <2 mm thick" },
  { code: "72091600", desc: "Cold-rolled coil — iron/non-alloy steel ≥600 mm, ≥3 mm thick" },
  { code: "72091700", desc: "Cold-rolled coil — iron/non-alloy steel ≥600 mm, 0.5–3 mm thick" },
  { code: "72091800", desc: "Cold-rolled coil — iron/non-alloy steel ≥600 mm, <0.5 mm thick" },
  { code: "72104900", desc: "Flat-rolled iron/non-alloy steel, zinc-coated, other" },
  { code: "72142000", desc: "Bars and rods — hot-rolled, with indentations or ribs (rebar)" },
  { code: "72151000", desc: "Bars and rods of free-cutting steel, not further worked than cold-formed" },
  { code: "72161000", desc: "Structural sections — U, I or H of iron/non-alloy steel (hot-rolled)" },
  { code: "72163100", desc: "Structural sections — H of iron/non-alloy steel (hot-rolled)" },
  { code: "72173010", desc: "Wire of stainless steel" },
  { code: "72182000", desc: "Bars and rods of stainless steel (hot-rolled, hot-drawn or extruded)" },
  { code: "72193300", desc: "Flat-rolled stainless steel ≥600 mm, 3–4.75 mm thick" },
  { code: "72199000", desc: "Flat-rolled stainless steel ≥600 mm, other" },
  { code: "73041100", desc: "Seamless pipes and tubes — line pipe for oil or gas" },
  { code: "73043100", desc: "Seamless pipes and tubes — cold-drawn or cold-rolled, circular cross-section" },
  { code: "73051200", desc: "Line pipe — longitudinal submerged arc welded (LSAW)" },
  { code: "73061100", desc: "Line pipe — longitudinal welded, for oil or gas, OD ≤406.4 mm" },
  { code: "73072200", desc: "Tube or pipe fittings — threaded elbows, bends and sleeves" },
  { code: "73082000", desc: "Towers and lattice masts of iron or steel" },
  { code: "73121000", desc: "Stranded wire, ropes and cables — iron or steel (non-electric)" },
];

// ── Aluminium ─────────────────────────────────────────────────────────────────
// Chapter 76. Common Indian exports: unwrought alloy, extrusions, rolled sheet,
// foil, tubes.

const ALUMINIUM = [
  { code: "76011000", desc: "Unwrought aluminium — not alloyed" },
  { code: "76012000", desc: "Unwrought aluminium — alloys" },
  { code: "76031000", desc: "Aluminium powders of non-lamellar structure" },
  { code: "76041000", desc: "Aluminium bars, rods and profiles — not alloyed" },
  { code: "76042100", desc: "Aluminium hollow profiles — alloys" },
  { code: "76042900", desc: "Aluminium bars, rods and profiles — alloys, other" },
  { code: "76051100", desc: "Aluminium wire — not alloyed, max cross-section >7 mm" },
  { code: "76061200", desc: "Aluminium plates, sheets and strip — alloys, rectangular" },
  { code: "76071100", desc: "Aluminium foil — not backed, rolled but not further worked" },
  { code: "76071900", desc: "Aluminium foil — not backed, other" },
  { code: "76082000", desc: "Aluminium tubes and pipes — alloys" },
  { code: "76090000", desc: "Aluminium tube or pipe fittings" },
  { code: "76101000", desc: "Aluminium doors, windows, frames and thresholds" },
  { code: "76151000", desc: "Aluminium table, kitchen or household articles" },
];

// ── Cement ────────────────────────────────────────────────────────────────────
// Chapter 25. Only 6 codes in CBAM. All included.

const CEMENT = [
  { code: "25231000", desc: "Cement clinkers" },
  { code: "25232100", desc: "White Portland cement — whether or not artificially coloured" },
  { code: "25232900", desc: "Portland cement — other" },
  { code: "25233000", desc: "Aluminous cement" },
  { code: "25239000", desc: "Other hydraulic cements" },
  { code: "25210000", desc: "Limestone flux; limestone for industrial use" },
];

// ── Fertilisers ───────────────────────────────────────────────────────────────
// Chapters 28, 31. 25 codes in CBAM; showing the most common for Indian exporters.

const FERTILISERS = [
  { code: "28080010", desc: "Nitric acid" },
  { code: "28080090", desc: "Sulphonitric acids" },
  { code: "28142000", desc: "Ammonia in aqueous solution" },
  { code: "28151100", desc: "Sodium hydroxide (caustic soda) — solid" },
  { code: "28342100", desc: "Potassium nitrate" },
  { code: "31021000", desc: "Urea — whether or not in aqueous solution" },
  { code: "31023000", desc: "Ammonium nitrate — whether or not in aqueous solution" },
  { code: "31025000", desc: "Sodium nitrate" },
  { code: "31026000", desc: "Double salts and mixtures of calcium nitrate and ammonium nitrate" },
  { code: "31028000", desc: "Mixtures of urea and ammonium nitrate in aqueous or ammoniacal solution" },
  { code: "31042000", desc: "Potassium chloride (muriate of potash)" },
  { code: "31051000", desc: "Goods of Chapter 31, in tablets or similar forms or in packages ≤10 kg" },
  { code: "31052000", desc: "Mineral or chemical fertilisers — N, P and K (triple)" },
  { code: "31054000", desc: "Diammonium hydrogenorthophosphate (DAP)" },
  { code: "31059000", desc: "Other mineral or chemical fertilisers" },
];

// ── Public API ────────────────────────────────────────────────────────────────

/** All CN codes indexed by sector. Matches organisations.sector values. */
export const SUCHI = {
  steel:       STEEL,
  aluminium:   ALUMINIUM,
  cement:      CEMENT,
  fertilisers: FERTILISERS,
};

/**
 * Returns the curated CN code list for a given sector.
 * Returns an empty array for unknown/null sectors (safe default).
 *
 * @param {string} sector — one of 'steel' | 'aluminium' | 'cement' | 'fertilisers'
 * @returns {{ code: string, desc: string }[]}
 */
export function getCodesForSector(sector) {
  return SUCHI[sector] ?? [];
}

/**
 * Filters a sector's codes by a search query.
 * Matches on code prefix OR case-insensitive description substring.
 *
 * @param {string} sector
 * @param {string} query — raw user input, trimmed by caller
 * @returns {{ code: string, desc: string }[]}
 */
export function searchCodes(sector, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return getCodesForSector(sector);
  return getCodesForSector(sector).filter(
    ({ code, desc }) =>
      code.startsWith(q) || desc.toLowerCase().includes(q)
  );
}
